<div class="site-header" [class.is-authenticated]="isAuthenticated">
  <div class="container header__container">
    <div class="header__bar">
      <!-- Zone 1: logo. Never shrinks; explicit size so the navigation does not shift when the image loads. -->
      <a class="header__brand" routerLink="/">
        <img src="assets/images/logo.svg" alt="Makani" width="106" height="32" />
      </a>

      <!-- Zone 2: primary navigation. Flexible, start-aligned after the logo, never wraps. Desktop only. -->
      <nav class="header__nav app-nav" [attr.aria-label]="'GENERAL.MAIN_NAVIGATION' | translate">
        <ul class="app-nav__list">
          @for (item of navItems; track item.id) {
            <li class="app-nav__item">
              @if (isGroup(item)) {
                <div ngbDropdown display="static" class="app-nav__group" [class.is-active]="isActive(item)">
                  <button
                    type="button"
                    ngbDropdownToggle
                    class="app-nav__link app-nav__toggle"
                    [attr.aria-controls]="'header-menu-' + item.id"
                    [attr.aria-current]="isActive(item) ? 'true' : null"
                  >
                    @let groupLabel = item.labelKey | translate;
                    <span class="app-nav__label" [attr.data-label]="groupLabel">{{ groupLabel }}</span>
                    <i class="pi pi-chevron-down app-nav__caret" aria-hidden="true"></i>
                  </button>
                  <div ngbDropdownMenu class="app-nav__menu" [id]="'header-menu-' + item.id">
                    @for (child of item.children; track child.id) {
                      <a
                        ngbDropdownItem
                        class="app-nav__menu-link"
                        [routerLink]="child.link"
                        [class.is-active]="isLinkActive(child)"
                        [attr.aria-current]="isLinkActive(child) ? 'page' : null"
                      >
                        <i [class]="child.icon" aria-hidden="true"></i>
                        <span>{{ child.labelKey | translate }}</span>
                      </a>
                    }
                  </div>
                </div>
              } @else {
                <a
                  class="app-nav__link"
                  [routerLink]="item.link"
                  [class.is-active]="isLinkActive(item)"
                  [attr.aria-current]="isLinkActive(item) ? 'page' : null"
                >
                  @let linkLabel = item.labelKey | translate;
                  <span class="app-nav__label" [attr.data-label]="linkLabel">{{ linkLabel }}</span>
                </a>
              }
            </li>
          }
        </ul>
      </nav>

      <!-- Zone 3: utilities. Fixed size, never shrinks and is never clipped. Each utility is a flex item with the
           shared 40px height, so another action (e.g. a notifications bell) can be added here without touching
           the layout. -->
      <div class="header__utilities">
        <app-switch-language
          class="header__lang"
          [ltrLangValue]="'EN'"
          [rtlLangValue]="'AR'"
          [ltrLangFlag]="'../../../assets/flags/us.svg'"
          [rtlLangFlag]="'../../../assets/flags/ar.svg'"
        ></app-switch-language>

        @if (isAuthenticated) {
          <app-notification-bill />

          <!-- Profile menu (desktop; on smaller screens the profile lives in the drawer) -->
          <div ngbDropdown display="static" class="header__profile">
            <button
              type="button"
              ngbDropdownToggle
              class="profilebtn"
              id="profileDropdown"
              aria-controls="header-profile-menu"
            >
              <span class="photoicon" aria-hidden="true"></span>
              <span class="profilename profilename-trimmed">{{ profileData?.firstName }}</span>
            </button>
            <div ngbDropdownMenu class="profile-menu" id="header-profile-menu" aria-labelledby="profileDropdown">
              <div class="profile-menu__summary">
                <span class="photoicon photoicon--lg" aria-hidden="true"></span>
                <p class="profile-menu__name">{{ profileData?.firstName }} {{ profileData?.lastName }}</p>
                @if (profileData?.phoneNumber; as phone) {
                  <p class="profile-menu__phone" dir="ltr">{{ phone.countryCode }} {{ phone.number }}</p>
                }
              </div>
              <!-- Account navigation (Vehicles, ...): see menu/account-menu.ts. Only the item itself shows the active
                   state, never the profile button. -->
              @for (item of accountNav; track item.id) {
                <a
                  ngbDropdownItem
                  class="profile-menu__item"
                  [routerLink]="item.link"
                  [class.is-active]="isLinkActive(item)"
                  [attr.aria-current]="isLinkActive(item) ? 'page' : null"
                >
                  <i [class]="item.icon" aria-hidden="true"></i>
                  <span>{{ item.labelKey | translate }}</span>
                </a>
              }
              <a
                ngbDropdownItem
                class="profile-menu__item"
                [routerLink]="profileRoute"
                [class.is-active]="isProfileActive()"
                [attr.aria-current]="isProfileActive() ? 'page' : null"
              >
                <i class="pi pi-user" aria-hidden="true"></i>
                <span>{{ 'GENERAL.PROFILE' | translate }}</span>
              </a>
              <div class="profile-menu__divider" role="separator"></div>
              <button type="button" ngbDropdownItem class="profile-menu__item" (click)="confirmLogOut($event)">
                <i class="pi pi-sign-out" aria-hidden="true"></i>
                <span>{{ 'GENERAL.LOG_OUT' | translate }}</span>
              </button>
            </div>
          </div>

          <!-- Wallet balance. The amount slot has a reserved width, so the skeleton, the balance and the empty state
               all occupy the same space. -->
          <button type="button" class="wallet-pill" [attr.aria-busy]="!walletResolved()" (click)="navigateToWallet()">
            <span class="visually-hidden">{{ 'WALLET.MY_WALLET' | translate }}</span>
            <img
              class="wallet-pill__icon"
              src="assets/images/icons/wallet-icon.svg"
              width="18"
              alt=""
              aria-hidden="true"
            />
            <app-riyal-symbol class="wallet-riyal-symbol" aria-hidden="true"></app-riyal-symbol>
            <span class="wallet-amount">
              @if (wallet(); as walletValue) {
                {{ walletValue.totalBalance.amount | number: '1.2-2' }}
              } @else if (walletResolved()) {
                —
              } @else {
                <p-skeleton height="0.875rem" />
              }
            </span>
          </button>
        } @else {
          <button type="button" class="btn btn-primary rounded-5 px-4 header__login" (click)="navigateToLogin()">
            {{ 'GENERAL.LOGIN' | translate }}
          </button>
        }

        <!-- Menu button (below the desktop breakpoint) -->
        <button
          #menuToggle
          class="header__toggler"
          type="button"
          aria-controls="mobile-nav-drawer"
          [attr.aria-expanded]="isMobileMenuOpen()"
          [attr.aria-label]="'GENERAL.MENU' | translate"
          (click)="toggleMobileMenu()"
        >
          <span class="toggler-bars" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>
    </div>
  </div>
</div>

@if (isMobileMenuOpen()) {
  <div class="mobile-nav-backdrop" aria-hidden="true" (click)="closeMobileMenu()"></div>
}

<!-- `inert` while closed keeps the off-screen drawer out of the tab order and the accessibility tree. -->
<aside
  id="mobile-nav-drawer"
  class="mobile-nav-drawer"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="'GENERAL.MAIN_NAVIGATION' | translate"
  [class.mobile-open]="isMobileMenuOpen()"
  [attr.inert]="isMobileMenuOpen() ? null : ''"
  [cdkTrapFocus]="isMobileMenuOpen()"
>
  <div class="mobile-nav-header">
    <a class="header__brand" routerLink="/" (click)="closeMobileMenu()">
      <img src="assets/images/logo.svg" alt="Makani" width="106" height="32" />
    </a>
    <button
      #drawerClose
      class="mobile-close"
      (click)="closeMobileMenu()"
      type="button"
      [attr.aria-label]="'GENERAL.CLOSE_MENU' | translate"
    >
      <i class="pi pi-times" aria-hidden="true"></i>
    </button>
  </div>

  <nav [attr.aria-label]="'GENERAL.MAIN_NAVIGATION' | translate">
    <ul class="mobile-nav-links">
      @for (item of navItems; track item.id) {
        @if (isGroup(item)) {
          <!-- Same hierarchy as the desktop menu. With this few children the group is always expanded. -->
          <li class="mobile-nav-group">
            <span class="mobile-nav-group__title" [id]="'mobile-nav-group-' + item.id">{{
              item.labelKey | translate
            }}</span>
            <ul class="mobile-nav-group__list" [attr.aria-labelledby]="'mobile-nav-group-' + item.id">
              @for (child of item.children; track child.id) {
                <li>
                  <a
                    class="mobile-nav-link mobile-nav-link--child"
                    [routerLink]="child.link"
                    [class.active]="isLinkActive(child)"
                    [attr.aria-current]="isLinkActive(child) ? 'page' : null"
                    (click)="closeMobileMenu()"
                  >
                    <i [class]="child.icon" aria-hidden="true"></i>
                    <span>{{ child.labelKey | translate }}</span>
                  </a>
                </li>
              }
            </ul>
          </li>
        } @else {
          <li>
            <a
              class="mobile-nav-link"
              [routerLink]="item.link"
              [class.active]="isLinkActive(item)"
              [attr.aria-current]="isLinkActive(item) ? 'page' : null"
              (click)="closeMobileMenu()"
            >
              {{ item.labelKey | translate }}
            </a>
          </li>
        }
      }
    </ul>
  </nav>

  <div class="mobile-control-group">
    @if (isAuthenticated) {
      <!-- Account block, in the same order as the profile menu on desktop: account navigation, profile, log out. -->
      @for (item of accountNav; track item.id) {
        <a
          class="mobile-account-link mobile-action-row"
          [routerLink]="item.link"
          [class.is-active]="isLinkActive(item)"
          [attr.aria-current]="isLinkActive(item) ? 'page' : null"
          (click)="closeMobileMenu()"
        >
          <span>{{ item.labelKey | translate }}</span>
          <i [class]="item.icon" aria-hidden="true"></i>
        </a>
      }

      <button
        class="mobile-profile-trigger mobile-action-row"
        type="button"
        [class.is-active]="isProfileActive()"
        [attr.aria-current]="isProfileActive() ? 'page' : null"
        (click)="navigateToProfile()"
      >
        <div class="photoicon" aria-hidden="true"></div>
        <div class="mobile-profile-copy">
          <span class="mobile-profile-name">{{ profileData?.firstName }} {{ profileData?.lastName }}</span>
          <span class="mobile-profile-email">{{ profileData?.email }}</span>
        </div>
      </button>

      <button class="mobile-logout-btn mobile-action-row" type="button" (click)="confirmLogOut($event)">
        <span>{{ 'GENERAL.LOG_OUT' | translate }}</span>
        <i class="pi pi-sign-out" aria-hidden="true"></i>
      </button>
    } @else {
      <button class="mobile-login-btn mobile-action-row" type="button" (click)="navigateToLogin()">
        <span>{{ 'GENERAL.LOGIN' | translate }}</span>
        <i class="pi pi-sign-in" aria-hidden="true"></i>
      </button>
    }
  </div>
</aside>
<p-confirmDialog #cd>
  <ng-template pTemplate="headless" let-message>
    <div class="flex flex-column align-items-center p-4 surface-overlay border-round confirmation-dialg">
      <div class="text-center icon">
        <i class="pi pi-question text-5xl"></i>
      </div>
      <span class="header">
        {{ message.header }}
      </span>
      <p class="mb-0 message">{{ message.message }}</p>
      <hr />
      <div class="dialog-footer d-flex justify-content-between">
        <button
          pButton
          label="{{ translate.instant('GENERAL.CONFIRM') }}"
          (click)="cd.accept()"
          class="w-8rem cancel-btn main-bg text-white"
        ></button>
        <button
          pButton
          label="{{ translate.instant('GENERAL.CANCEL') }}"
          (click)="cd.reject()"
          class="p-button-outlined w-8rem cancel-btn"
        ></button>
      </div>
    </div>
  </ng-template>
</p-confirmDialog>
